import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CurrencyModel, UserModel, UsersClient } from 'src/api/services/generated/api-clients';

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  private usersClient = inject(UsersClient);

  private currency = signal<string>('EUR');
  private currencySymbol = signal<string>('€');

  private _currentUser = signal<UserModel | null>(null);

  async loadCurrentUser() {
    const user = await firstValueFrom(this.usersClient.getCurrentUser());
    this._currentUser.set(user.data);
  }

  getCurrency() {
    return this.currency();
  }

  getCurrencySymbol() {
    return this.currencySymbol();
  }

  setCurrency(currency: CurrencyModel) {
    this.currency.set(currency.isoCurrencyCode);
    this.currencySymbol.set(currency.currencySymbol);
  }

  public readonly isSuperAdmin = computed(() => this._currentUser()?.isSuperAdmin ?? false);

  public readonly currentUser = computed(() => this._currentUser());

  public readonly divisionIds = computed(() => this._currentUser()?.divisionIds ?? []);

  public readonly regionIds = computed(() => this._currentUser()?.regionIds ?? []);

  /**
   * The division to preselect from the list: the last one the user chose if it is still in the list (only with more than one
   * division), otherwise the first of the user's divisions, otherwise the first division.
   */
  public defaultDivisionId(divisions: readonly { id: string }[]): string | undefined {
    const lastDivisionId = this.readLastDivisionId();
    if (divisions.length > 1 && divisions.some((division) => division.id === lastDivisionId)) {
      return lastDivisionId!;
    }

    const userDivisionIds = this.divisionIds();
    return (divisions.find((division) => userDivisionIds.includes(division.id)) ?? divisions[0])?.id;
  }

  /**
   * Remembers the division the user chose (per user, in this browser) for {@link defaultDivisionId}.
   */
  public rememberDivision(divisionId: string): void {
    try {
      localStorage.setItem(this.lastDivisionKey(), divisionId);
    } catch {
      // Without storage (e.g. blocked site data) the default stays the first division.
    }
  }

  private readLastDivisionId(): string | null {
    try {
      return localStorage.getItem(this.lastDivisionKey());
    } catch {
      return null;
    }
  }

  private lastDivisionKey(): string {
    return `investtool.lastDivisionId.${this._currentUser()?.userName ?? ''}`;
  }
}
